/**
 * Legal Documents Service
 * Manages Terms and Conditions and Privacy Policy
 */

import { supabase } from './supabase'

/**
 * Get Terms and Conditions
 */
export const getTermsAndConditions = async () => {
  try {
    // Try to fetch from database first
    try {
      const { data, error } = await supabase.rpc('get_latest_legal_document', {
        doc_type: 'terms_and_conditions'
      })

      if (!error && data && data.length > 0) {
        const doc = data[0]
        return {
          success: true,
          data: {
            title: doc.title || 'Terms and Conditions',
            lastUpdated: doc.effective_date || doc.created_at,
            content: doc.content,
            version: doc.version,
            url: '/legal/terms',
          },
        }
      }
    } catch (dbError) {
      console.warn('Could not fetch from database, using fallback:', dbError)
    }

    // Fallback: Import the markdown file content
    // Note: In production, this should be stored in the database
    const termsContent = `# TERMS AND CONDITIONS OF SERVICE

**Last Updated: December 23, 2024**

## IMPORTANT NOTICE

Please read these Terms and Conditions carefully before using the NaturAssist Service. These Terms contain important information about:

- **Pricing and Payment Terms** (Section 3) - Subscription plans, pricing, payment methods, billing cycles, upgrades, downgrades, and refunds
- **Subscription Cancellation** (Section 9.1) - How to cancel, when cancellations take effect
- **Subscription Upgrades** (Section 3.5) - How upgrades work, prorating, immediate effect
- **Termination** (Section 9.2) - When and how the Service may be terminated
- **Data Upon Cancellation** (Section 9.4) - Data export, retention, deletion policies
- **Limitation of Liability** (Section 7) - Important limitations on the Company's liability
- **Indemnification** (Section 7.3) - Your obligations to indemnify the Company

By using the Service, you agree to be bound by all these Terms, including the limitation of liability and indemnification provisions.

---

## 1. ACCEPTANCE OF TERMS

By accessing, using, or subscribing to the NaturAssist platform ("the Service", "the Platform", "we", "us", "our") operated by CwickCraft Designs PTY (LTD) ("the Company"), you ("the User", "Subscriber", "you", "your") agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use the Service.

These Terms constitute a legally binding agreement between you and the Company, governed by the laws of the Republic of South Africa.

## 2. DEFINITIONS

- **"Service"** means the NaturAssist software platform, including all features, functionality, and services provided.
- **"Subscriber"** means any individual or entity that has subscribed to or uses the Service.
- **"Organization"** means a business entity or practice that subscribes to the Service.
- **"User"** means any individual authorized by a Subscriber to access the Service.
- **"Content"** means any data, information, text, files, or other materials uploaded, stored, or transmitted through the Service.
- **"Client Data"** means any personal or health information of clients entered into the Service by Subscribers.

## 3. SUBSCRIPTION AND PAYMENT

### 3.1 Subscription Plans and Pricing

The Service is offered on a subscription basis with the following tiers:

- **Lite Plan**: R149/month (R1,490/year) - For solo professionals
- **Essential Plan**: R349/month (R3,490/year) - For small practices
- **Professional Plan**: R699/month (R6,990/year) - For growing practices
- **Enterprise Plan**: R1,499/month (R14,990/year) - For large organizations
- **White Label Plan**: Custom pricing - Contact sales for details
- **Demo Plan**: Free - For demonstration purposes only

All prices are in South African Rand (ZAR) and are exclusive of Value Added Tax (VAT) and other applicable taxes, duties, and levies.

**Annual Subscriptions**: Annual subscriptions are offered at a discounted rate (equivalent to 10 months of monthly payments), providing 2 months free when paying annually.

### 3.2 Pricing Changes

- The Company reserves the right to modify subscription prices at any time.
- Price changes for existing Subscribers will be communicated via email at least 30 days before the change takes effect.
- Price changes will apply to the next billing cycle after the notice period.
- If a Subscriber does not agree to a price increase, they may cancel their subscription before the price change takes effect.
- New Subscribers will be charged the current pricing at the time of subscription.

### 3.3 Payment Terms

**Payment Methods**:
- Payment must be made via approved payment methods, including:
  - Credit cards (Visa, Mastercard, American Express)
  - Debit cards
  - Bank transfers (for annual subscriptions)
  - Other payment methods as approved by the Company

**Payment Processing**:
- All fees are payable in advance of the subscription period.
- Payment is processed immediately upon subscription or renewal.
- Subscribers authorize the Company to charge the payment method on file for recurring subscriptions.
- Subscribers must maintain valid payment information on their account.

**Currency and Taxes**:
- All prices are displayed in South African Rand (ZAR).
- Subscribers are responsible for all applicable taxes, duties, levies, and fees, including VAT.
- The Company will provide tax invoices for all payments where required by law.

**Payment Authorization**:
- By providing payment information, Subscribers authorize the Company to charge the payment method for:
  - Initial subscription fees
  - Renewal fees
  - Upgrade fees (prorated or full, as applicable)
  - Any additional fees or charges as outlined in these Terms

### 3.4 Billing Cycles

**Monthly Subscriptions**:
- Monthly subscriptions are billed on the same date each month (based on the initial subscription date).
- Monthly subscriptions renew automatically unless cancelled.
- Billing occurs on or before the renewal date.

**Annual Subscriptions**:
- Annual subscriptions are billed on the anniversary date of the initial subscription.
- Annual subscriptions renew automatically unless cancelled.
- Billing occurs on or before the renewal date.

**Billing Period**:
- The billing period begins on the date of payment and ends on the day before the next billing date.
- Access to the Service is provided for the entire billing period, regardless of cancellation timing.

### 3.5 Subscription Upgrades

**Upgrading Your Plan**:
- Subscribers may upgrade their subscription plan at any time through the Service dashboard.
- Upgrades take effect immediately upon successful payment.
- When upgrading mid-cycle:
  - Subscribers are charged a prorated amount for the remainder of the current billing period.
  - The prorated amount is calculated based on the difference between the current and new plan prices.
  - The new plan's full price applies to the next billing cycle.

**Upgrade Payment**:
- Payment for upgrades is processed immediately via the same payment method on file.
- Upgraded features and limits become available immediately upon payment confirmation.
- No refunds are provided for the unused portion of the previous plan when upgrading.

**Upgrade Limitations**:
- Upgrades are subject to payment verification and approval.
- The Company reserves the right to require additional verification for significant upgrades.
- Upgrades may be subject to availability and eligibility requirements.

### 3.6 Subscription Downgrades

**Downgrading Your Plan**:
- Subscribers may downgrade their subscription plan at any time.
- Downgrades take effect at the end of the current billing period.
- No refunds are provided for the unused portion of the current plan when downgrading.
- Subscribers must ensure their usage (users, clients, storage) complies with the new plan's limits before the downgrade takes effect.

**Downgrade Limitations**:
- If current usage exceeds the new plan's limits, the downgrade may be delayed until compliance is achieved.
- The Company may suspend access to features that exceed the new plan's limits.
- Data may need to be archived or deleted to comply with the new plan's limits.

### 3.7 Failed Payments and Payment Issues

**Failed Payment Attempts**:
- If a payment fails, the Company will attempt to process the payment again.
- Subscribers will be notified via email of failed payment attempts.
- Multiple failed payment attempts may result in:
  - Suspension of Service access
  - Termination of subscription
  - Additional fees for failed payment processing

**Payment Retry Policy**:
- The Company will attempt to process failed payments up to 3 times over a 7-day period.
- Subscribers are responsible for updating payment information if payment continues to fail.
- Service access may be suspended during the retry period.

**Outstanding Balances**:
- All outstanding fees remain due and payable, regardless of Service suspension or termination.
- The Company reserves the right to:
  - Charge reasonable fees for failed payment attempts and collection efforts
  - Engage collection agencies or legal proceedings to recover outstanding amounts
  - Report outstanding debts to credit bureaus where permitted by law

**Payment Disputes**:
- Subscribers must notify the Company of any payment disputes within 30 days of the charge.
- The Company will investigate disputes and provide a response within 14 business days.
- Service access may be suspended during dispute resolution if payment is withheld.

### 3.8 Refunds

**Refund Policy**:
- Subscription fees are non-refundable except as required by South African law or as specifically stated in these Terms.
- Refunds are not provided for:
  - Unused portions of subscription periods
  - Downgrades or cancellations mid-cycle
  - Changes of mind or dissatisfaction with the Service
  - Features that do not meet specific requirements

**Refund Eligibility**:
- Refunds may be considered in exceptional circumstances, including:
  - Duplicate charges due to system errors
  - Charges made in error by the Company
  - Service unavailability exceeding 48 consecutive hours (proportional refund)
  - As required by South African consumer protection laws

**Refund Process**:
- Refund requests must be submitted in writing to legal@cwickcraft.com within 30 days of the charge.
- Refunds, if approved, will be processed within 30 business days.
- Refunds will be issued to the original payment method used.
- Processing fees may be deducted from refunds where applicable.

## 4. USE OF SERVICE

### 4.1 Authorized Use
Subscribers may use the Service solely for lawful business purposes related to health management and client care. The Service must not be used for:
- Any illegal or unauthorized purpose.
- Violation of any applicable laws or regulations.
- Infringement of intellectual property rights.
- Transmission of malicious code, viruses, or harmful data.
- Unauthorized access to other systems or networks.

### 4.2 Account Security
- Subscribers are responsible for maintaining the confidentiality of account credentials.
- Subscribers must immediately notify the Company of any unauthorized access.
- The Company is not liable for losses resulting from unauthorized access due to Subscriber negligence.

### 4.3 User Limits
Subscription tiers include limits on the number of users, clients, and storage. Exceeding these limits may result in:
- Automatic upgrade to a higher tier.
- Suspension of service until limits are complied with.
- Additional fees for excess usage.

## 5. DATA AND PRIVACY

### 5.1 Data Ownership
Subscribers retain ownership of all data entered into the Service. The Company does not claim ownership of Subscriber data.

### 5.2 Data Storage and Security
- The Company implements reasonable security measures to protect data.
- Data is stored on secure servers, which may be located outside South Africa.
- The Company is not liable for data loss, corruption, or unauthorized access except where caused by gross negligence.

### 5.3 Data Backup
- The Company performs regular backups but does not guarantee data recovery.
- Subscribers are responsible for maintaining their own backups of critical data.
- The Company is not liable for data loss or inability to restore data.

### 5.4 Compliance with POPIA
The Company complies with the Protection of Personal Information Act (POPIA) of South Africa. Subscribers are responsible for:
- Obtaining necessary consents from clients before entering their data.
- Complying with POPIA requirements in their use of the Service.
- Ensuring data accuracy and relevance.

### 5.5 Data Deletion and Retention
- Upon subscription cancellation, Subscribers may request data deletion after exporting their data.
- The Company will delete data within 30 days of request, subject to legal retention requirements.
- See Section 9.4 for detailed information on data handling upon cancellation or termination.

## 6. INTELLECTUAL PROPERTY

### 6.1 Company Rights
All intellectual property rights in the Service, including software, design, trademarks, and documentation, remain the exclusive property of the Company.

### 6.2 License Grant
The Company grants Subscribers a limited, non-exclusive, non-transferable, revocable license to use the Service during the subscription period.

### 6.3 Restrictions
Subscribers may not:
- Copy, modify, or create derivative works of the Service.
- Reverse engineer, decompile, or disassemble the Service.
- Remove or alter copyright notices or proprietary markings.
- Use the Service to develop competing products or services.

## 7. LIMITATION OF LIABILITY AND INDEMNIFICATION

### 7.1 DISCLAIMER OF WARRANTIES
THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
- WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
- WARRANTIES THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
- WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR COMPLETENESS OF DATA OR INFORMATION.

### 7.2 LIMITATION OF LIABILITY
TO THE MAXIMUM EXTENT PERMITTED BY SOUTH AFRICAN LAW:
- THE COMPANY'S TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT PAID BY THE SUBSCRIBER IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
- THE COMPANY SHALL NOT BE LIABLE FOR:
  - INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
  - LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES.
  - DAMAGES ARISING FROM USE OR INABILITY TO USE THE SERVICE.
  - DAMAGES ARISING FROM UNAUTHORIZED ACCESS OR DATA BREACHES.
  - DAMAGES ARISING FROM CLINICAL DECISIONS MADE USING THE SERVICE.

### 7.3 INDEMNIFICATION
SUBSCRIBERS AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS THE COMPANY, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES FROM AND AGAINST ALL CLAIMS, DAMAGES, LOSSES, LIABILITIES, COSTS, AND EXPENSES (INCLUDING REASONABLE ATTORNEYS' FEES) ARISING FROM:
- SUBSCRIBER'S USE OR MISUSE OF THE SERVICE.
- SUBSCRIBER'S VIOLATION OF THESE TERMS.
- SUBSCRIBER'S VIOLATION OF ANY LAW OR REGULATION.
- SUBSCRIBER'S INFRINGEMENT OF ANY THIRD-PARTY RIGHTS.
- CLINICAL DECISIONS OR ACTIONS TAKEN BY SUBSCRIBERS OR THEIR USERS.
- DATA ENTERED BY SUBSCRIBERS OR THEIR USERS.
- ANY CLAIMS BY SUBSCRIBER'S CLIENTS OR THIRD PARTIES.

### 7.4 MEDICAL DISCLAIMER
THE SERVICE IS A DATA MANAGEMENT TOOL AND DOES NOT PROVIDE MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT. SUBSCRIBERS ARE SOLELY RESPONSIBLE FOR:
- ALL CLINICAL DECISIONS AND MEDICAL JUDGMENTS.
- COMPLIANCE WITH ALL APPLICABLE MEDICAL, HEALTHCARE, AND PROFESSIONAL STANDARDS.
- OBTAINING APPROPRIATE PROFESSIONAL LICENSES AND QUALIFICATIONS.
- VERIFYING THE ACCURACY AND APPROPRIATENESS OF ALL DATA AND INFORMATION.

THE COMPANY SHALL NOT BE LIABLE FOR ANY ADVERSE OUTCOMES, INJURIES, OR DAMAGES RESULTING FROM CLINICAL DECISIONS MADE USING THE SERVICE.

## 8. SERVICE AVAILABILITY AND MODIFICATIONS

### 8.1 Service Availability
- The Company strives to maintain Service availability but does not guarantee uninterrupted or error-free operation.
- The Company may perform scheduled maintenance with reasonable notice.
- The Company is not liable for Service unavailability due to:
  - Maintenance, updates, or upgrades.
  - Force majeure events.
  - Third-party service failures.
  - Network or internet connectivity issues.

### 8.2 Service Modifications
The Company reserves the right to:
- Modify, update, or discontinue features at any time.
- Change pricing with 30 days' notice.
- Suspend or terminate Service for violations of these Terms.
- Refuse service to any person or entity.

## 9. SUBSCRIPTION CANCELLATION AND TERMINATION

### 9.1 Cancellation by Subscriber

**How to Cancel**:
- Subscribers may cancel their subscription at any time through:
  - The Service dashboard (Organization Settings → Billing)
  - Written notice via email to support@cwickcraft.com
  - Written notice via postal mail to the Company's registered address

**Cancellation Timing**:
- Cancellation requests must be received at least 24 hours before the next billing date to prevent automatic renewal.
- Cancellations take effect at the end of the current billing period.
- Subscribers retain full access to the Service until the end of the paid billing period.

**Immediate Cancellation**:
- Subscribers may request immediate cancellation, which will:
  - Terminate access immediately
  - Not provide a refund for the unused portion of the billing period
  - Still allow data export for 30 days (see Section 9.4)

**Cancellation Confirmation**:
- The Company will send a confirmation email upon receipt of cancellation request.
- Cancellation is effective upon receipt of the confirmation email or at the end of the billing period, whichever is later.

### 9.2 Termination by Company

**Immediate Termination**:
The Company may suspend or terminate Service immediately without notice if:
- Subscriber breaches these Terms or the Privacy Policy
- Payment is overdue or payment method fails repeatedly
- Subscriber engages in illegal, fraudulent, or harmful activities
- Subscriber violates intellectual property rights
- Subscriber uses the Service in a manner that threatens the security or integrity of the Service
- Required by law, regulation, or court order
- Subscriber becomes insolvent, bankrupt, or enters liquidation

**Termination with Notice**:
The Company may terminate Service with 30 days' written notice if:
- Subscriber fails to remedy a breach after receiving notice
- The Company discontinues the Service (with pro-rata refund for prepaid periods)
- Required by regulatory changes or business decisions

**Suspension**:
- The Company may suspend Service access temporarily:
  - During investigation of suspected violations
  - During payment disputes or failed payments
  - For maintenance or security reasons
  - As required by law or regulation

### 9.3 Effect of Termination or Cancellation

**Immediate Effects**:
- Upon termination (immediate or at end of billing period):
  - Access to the Service will be suspended or terminated
  - All users associated with the Organization will lose access
  - Active sessions will be terminated
  - New data cannot be entered or modified

**Data Access**:
- Subscribers retain access to their data for export purposes for 30 days after termination/cancellation.
- After 30 days, data may be permanently deleted (see Section 9.4).
- The Company is not obligated to maintain data beyond the 30-day period.

**Outstanding Obligations**:
- All outstanding fees, charges, and amounts due remain payable immediately upon termination.
- Termination does not relieve Subscribers of obligations incurred before termination.
- The Company may pursue collection of outstanding amounts through legal means.

**Surviving Provisions**:
- The following provisions survive termination:
  - Payment obligations and outstanding fees
  - Indemnification obligations (Section 7.3)
  - Limitation of liability (Section 7.2)
  - Intellectual property rights (Section 6)
  - Dispute resolution (Section 12)
  - Data retention and deletion obligations (Section 9.4)

### 9.4 Data Upon Cancellation or Termination

**Data Export Period**:
- Subscribers have 30 days from the date of cancellation/termination to export their data.
- Data export functionality is available through the Service dashboard during this period.
- The Company will provide reasonable assistance with data export upon request.

**Data Export Format**:
- Data may be exported in standard formats (CSV, JSON, PDF) as available in the Service.
- The Company will provide data in a structured, machine-readable format where possible.
- Custom export formats may be available upon request (subject to additional fees).

**Data Retention**:
- After the 30-day export period, the Company may:
  - Permanently delete all Subscriber data from active systems
  - Archive data for legal or regulatory compliance purposes
  - Delete data in accordance with data retention policies

**Legal Retention Requirements**:
- The Company may retain certain data beyond the 30-day period if required by:
  - South African law or regulations
  - Court orders or legal proceedings
  - Regulatory compliance requirements
  - POPIA and other data protection laws

**Data Deletion**:
- Upon request and after the 30-day export period, the Company will:
  - Delete data from active systems within 30 days of the deletion request
  - Delete data from backup systems within 90 days (where technically feasible)
  - Provide confirmation of deletion upon request

**Client Data Responsibility**:
- Subscribers are solely responsible for:
  - Exporting client data before cancellation/termination
  - Notifying clients of data deletion if required by law
  - Complying with data protection laws regarding client data
  - Obtaining necessary consents for data export or transfer

**Backup Data**:
- The Company maintains backups for disaster recovery purposes.
- Backup data may be retained for longer periods as part of backup rotation schedules.
- Subscribers cannot request deletion of backup data until it is automatically purged in accordance with backup retention policies.

**Third-Party Integrations**:
- Data stored in third-party services (integrations) may be subject to separate retention policies.
- Subscribers are responsible for managing data in third-party services independently.

**No Data Recovery After Deletion**:
- Once data is deleted, it cannot be recovered.
- The Company is not liable for data loss after the 30-day export period.
- Subscribers should export all critical data before cancellation/termination.

### 9.5 Post-Termination Obligations

**Confidentiality**:
- Subscribers must maintain confidentiality of any proprietary information accessed during the subscription period.
- This obligation continues indefinitely after termination.

**Return of Materials**:
- Subscribers must return or destroy any Company materials, documentation, or proprietary information in their possession.
- This includes any downloaded documentation, API keys, or access credentials.

**Cooperation**:
- Subscribers agree to cooperate with the Company in:
  - Data export and migration processes
  - Investigation of any breaches or violations
  - Transition of services to other providers

## 10. THIRD-PARTY SERVICES

The Service may integrate with or link to third-party services. The Company:
- Does not endorse or assume responsibility for third-party services.
- Is not liable for third-party service failures or issues.
- Does not guarantee compatibility with third-party services.

## 11. FORCE MAJEURE

The Company shall not be liable for failure to perform obligations due to circumstances beyond reasonable control, including:
- Natural disasters, pandemics, or acts of God.
- War, terrorism, or civil unrest.
- Government actions or regulations.
- Internet or telecommunications failures.
- Cyberattacks or security breaches.

## 12. DISPUTE RESOLUTION

### 12.1 Governing Law
These Terms are governed by the laws of the Republic of South Africa, without regard to conflict of law principles.

### 12.2 Jurisdiction
Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of South Africa.

### 12.3 Dispute Resolution Process
Before initiating legal proceedings, parties agree to:
1. Attempt good faith negotiation for 30 days.
2. If unsuccessful, attempt mediation through a mutually agreed mediator.
3. Only if mediation fails, proceed to litigation.

## 13. GENERAL PROVISIONS

### 13.1 Entire Agreement
These Terms, together with the Privacy Policy, constitute the entire agreement between parties and supersede all prior agreements.

### 13.2 Amendments
The Company may amend these Terms at any time. Material changes will be notified via email or Service notification. Continued use after notification constitutes acceptance.

### 13.3 Severability
If any provision is found unenforceable, the remaining provisions shall remain in full effect.

### 13.4 Waiver
Failure to enforce any provision does not constitute a waiver of that provision.

### 13.5 Assignment
Subscribers may not assign or transfer these Terms without written consent. The Company may assign these Terms to affiliates or successors.

### 13.6 Notices
Notices must be in writing and delivered via email to the registered email address or through the Service dashboard.

### 13.7 Relationship
These Terms do not create a partnership, joint venture, or employment relationship between parties.

## 14. CONTACT INFORMATION

For questions regarding these Terms, contact:

**Company Name**: CwickCraft Designs PTY (LTD)
**Email**: legal@cwickcraft.com
**Address**: Midrand, South Africa
**Phone (WhatsApp)**: 0746502694

---

**By using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.**`

    return {
      success: true,
      data: {
        title: 'Terms and Conditions',
        lastUpdated: '2024-12-23T00:00:00.000Z',
        content: termsContent,
        version: '1.0',
        url: '/legal/terms',
      },
    }
  } catch (error) {
    console.error('Error getting Terms and Conditions:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get Privacy Policy
 */
export const getPrivacyPolicy = async () => {
  try {
    return {
      success: true,
      data: {
        title: 'Privacy Policy',
        lastUpdated: new Date().toISOString(),
        content: null,
        url: '/legal/privacy',
      },
    }
  } catch (error) {
    console.error('Error getting Privacy Policy:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Record acceptance of Terms and Conditions
 */
export const recordTermsAcceptance = async (userId, organizationId, version) => {
  try {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .insert({
        user_id: userId,
        organization_id: organizationId,
        document_type: 'terms_and_conditions',
        version: version,
        accepted_at: new Date().toISOString(),
        ip_address: null, // Could capture IP if needed
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error recording terms acceptance:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Record acceptance of Privacy Policy
 */
export const recordPrivacyAcceptance = async (userId, organizationId, version) => {
  try {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .insert({
        user_id: userId,
        organization_id: organizationId,
        document_type: 'privacy_policy',
        version: version,
        accepted_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error recording privacy acceptance:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if user has accepted latest Terms
 */
export const checkTermsAcceptance = async (userId, organizationId) => {
  try {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .select('*')
      .eq('user_id', userId)
      .eq('organization_id', organizationId)
      .eq('document_type', 'terms_and_conditions')
      .order('accepted_at', { ascending: false })
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows
    return { success: true, data: data || null }
  } catch (error) {
    console.error('Error checking terms acceptance:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if user has accepted latest Privacy Policy
 */
export const checkPrivacyAcceptance = async (userId, organizationId) => {
  try {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .select('*')
      .eq('user_id', userId)
      .eq('organization_id', organizationId)
      .eq('document_type', 'privacy_policy')
      .order('accepted_at', { ascending: false })
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows
    return { success: true, data: data || null }
  } catch (error) {
    console.error('Error checking privacy acceptance:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if organization has accepted terms (checks if any admin has accepted)
 */
export const checkOrganizationTermsAcceptance = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('document_type', 'terms_and_conditions')
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows
    return { success: true, data: data || null }
  } catch (error) {
    console.error('Error checking organization terms acceptance:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if organization has accepted privacy policy
 */
export const checkOrganizationPrivacyAcceptance = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('legal_acceptances')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('document_type', 'privacy_policy')
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows
    return { success: true, data: data || null }
  } catch (error) {
    console.error('Error checking organization privacy acceptance:', error)
    return { success: false, error: error.message }
  }
}

export default {
  getTermsAndConditions,
  getPrivacyPolicy,
  recordTermsAcceptance,
  recordPrivacyAcceptance,
  checkTermsAcceptance,
  checkPrivacyAcceptance,
  checkOrganizationTermsAcceptance,
  checkOrganizationPrivacyAcceptance,
}

